import React from 'react'
import BeforeAfterProjectLimitsSection from './BeforeAfterProjectLimitsSection'
import ChangePaymentInterval from './ChangePaymentInterval'
import { PaymentInterval, RecurlyPrice } from './Recurly'
import SelectTideways6Plans from './SelectTideways6Plans'
import Summary from './Summary'
import { UpgradeTideways6, UpgradeTideways6Application } from './UpgradeTideways6'

interface Props {
    recurlyPricing: any
    upgrade: UpgradeTideways6
    actionUrl: string
    csrfToken: string
}
interface StateProps {
    projectSizes: { [key: string]: number }
    newPlan: string
    price: RecurlyPrice | null
    teamAddons: number
    interval: PaymentInterval
}

class UpgradeOrganization extends React.Component<Props, StateProps> {
    constructor(props: Props) {
        super(props)
        this.state = {
            projectSizes: props.upgrade.projectSizes,
            newPlan: props.upgrade.newPlan,
            price: null,
            teamAddons: 0,
            interval: props.upgrade.currentPlan.endsWith('_1y') ? PaymentInterval.Yearly : PaymentInterval.Monthly,
        }
    }
    public render() {
        const includedTeams = {
            tideways6_starter: 1,
            tideways6_basic: 1,
            tideways6_standard: 1,
            tideways6_pro: 5,
        }
        const requiredTeamAddons = Math.max(0, this.props.upgrade.teamsCount - includedTeams[this.state.newPlan])
        const membersUrl = this.props.actionUrl.replace('/billing/upgrade', '/members')
        const newPlan = this.state.interval == PaymentInterval.Yearly ? this.state.newPlan + '_1y' : this.state.newPlan

        return (
            <div className="row">
                <div className="col-lg-12">
                    <form className="form-horizontal" method="POST" action={this.props.actionUrl}>
                        <input type="hidden" name="_csrf[billing]" value={this.props.csrfToken} />
                        <div className="box">
                            <div className="box-body">
                                <p>
                                    On this screen you can perform a self-service upgrade to Tideways 6. If you would like our support to
                                    guide you with the upgrade please send an email to:{' '}
                                    <a href="mailto:support@tideways.com">support@tideways.com</a>. Based on your current usage of Tideways
                                    we suggest you perform the following upgrade. You can change the plan and project sizes to select the
                                    Increased Limits Add-On.
                                    {this.props.upgrade.applications.length > 1 ? (
                                        <span>
                                            If you don&apos;t want to keep all of the projects, delete them on their project settings page.
                                        </span>
                                    ) : null}
                                </p>
                            </div>
                        </div>
                        <h3>1. Select Plan</h3>
                        <SelectTideways6Plans
                            upgrade={this.props.upgrade}
                            onPlanChange={this.onUpdatePlan}
                            selectedPlan={this.state.newPlan}
                            recurlyPricing={this.props.recurlyPricing}
                            interval={this.state.interval}
                        />
                        <h3>2. Review Projects</h3>
                        <div className="box">
                            <div className="box-body">
                                {this.props.upgrade.applications.map((application, id) =>
                                    this.renderApplication(application, this.props.upgrade.applications.length === 1),
                                )}
                            </div>
                        </div>
                        <div className="row">
                            <div className="col-lg-6">
                                <h3>3. Review Teams</h3>
                                <div className="box">
                                    <div className="box-body">
                                        You currently have <a href={membersUrl}>created {this.props.upgrade.teamsCount} teams</a> in this
                                        organization. The selected plan includes {includedTeams[this.props.upgrade.newPlan]} team.{' '}
                                        <strong>{requiredTeamAddons}</strong> team add-ons are added to your plan.
                                        {requiredTeamAddons > 0 ? (
                                            <p>If you do not want to purchase team add-ons, you can remove some of your teams.</p>
                                        ) : null}
                                    </div>
                                </div>
                            </div>
                            <div className="col-md-6">
                                <h3>4. How often do you want to be billed?</h3>

                                <ChangePaymentInterval interval={this.state.interval} callback={interval => this.setState({ interval })} />
                            </div>
                        </div>
                        <Summary
                            recurlyPricing={this.props.recurlyPricing}
                            upgrade={this.props.upgrade}
                            newPlan={this.state.newPlan}
                            coupon={this.props.upgrade.coupon}
                            currency={this.props.upgrade.currency}
                            projectSizes={this.state.projectSizes}
                            interval={this.state.interval}
                        />
                        <input type="hidden" name="plan" data-recurly="plan" value={newPlan} />
                        {this.props.upgrade.applications.map((application, id) => {
                            return (
                                <input
                                    key={application.id}
                                    type="hidden"
                                    name={`projectSizes[${application.id}]`}
                                    value={this.state.projectSizes[application.id]}
                                />
                            )
                        })}
                        <div className="box-footer centered">
                            <button type="submit" id="signup" className="btn btn-primary">
                                Upgrade to Tideways6 with obligation to pay
                            </button>
                        </div>
                    </form>
                </div>
            </div>
        )
    }

    public renderApplication(application: UpgradeTideways6Application, onlyApplication = false) {
        const min = onlyApplication ? 2 : 0
        return (
            <div key={application.id}>
                <h4>Project {application.name}</h4>

                <div className="row">
                    <div className="col-lg-6">
                        <p>
                            The{' '}
                            <a href="https://support.tideways.com/documentation/account-and-billing/billing-and-invoices/project-sizes.html?utm_medium=app&utm_source=upgrade-organization&utm_campaign=project-sizes">
                                selected project size
                            </a>{' '}
                            determines the number of services, environments, transactions and traces per minute. Select project size 0 to
                            convert a project into a profiling space.
                        </p>

                        <div className="form-group">
                            <label className="col-lg-4 control-label" htmlFor="changeProjectSize">
                                Project Size:
                            </label>
                            <div className="col-lg-1 control-label">
                                <span>{this.state.projectSizes[application.id]}</span>
                            </div>
                            <div className="col-lg-7">
                                <input
                                    type="range"
                                    className="form-control"
                                    min={min}
                                    max="25"
                                    value={this.state.projectSizes[application.id]}
                                    onChange={event => {
                                        this.setState({
                                            projectSizes: {
                                                ...this.state.projectSizes,
                                                [String(application.id)]: parseInt(event.currentTarget.value, 10),
                                            },
                                        })
                                    }}
                                />
                            </div>
                        </div>
                    </div>
                    <div className="col-lg-6">
                        {this.state.projectSizes[application.id] > 0 ? (
                            <BeforeAfterProjectLimitsSection
                                onlyAfter={false}
                                upgrade={this.props.upgrade}
                                projectId={String(application.id)}
                                projectSize={this.state.projectSizes[application.id]}
                                plan={this.state.newPlan}
                            />
                        ) : null}
                        {this.state.projectSizes[application.id] === 0 ? (
                            <p>
                                This project is converted into a{' '}
                                <a
                                    href="https://support.tideways.com/documentation/features/tracing-and-timeline-profiler/profiling-spaces.html?utm_medium=app&utm_source=upgrade-organization&utm_campaign=profiling-spaces"
                                    target="_blank"
                                    rel="noreferrer"
                                >
                                    profiling space
                                </a>{' '}
                                during the upgrade.
                            </p>
                        ) : null}
                    </div>
                </div>
            </div>
        )
    }

    private onUpdatePlan = plan => {
        this.setState({ newPlan: plan })
    }
}

export default UpgradeOrganization
